/**
 * M83 agreements — SUBJECT-facing service.
 *
 * Every call here is made by a vendor or customer about ITSELF. None requires platform
 * authority or platform enrolment. The platform-operator surface lives in `platform-api.ts`
 * and must never be reached from a subject screen.
 *
 * Transport paths carry NO `/api/v1` prefix: the base URL already includes it.
 */
import { z } from 'zod';
import { http, generateIdempotencyKey } from '@/api/client/http';
import { ResponseMetaSchema } from '@/api/schemas/common';
import {
  AgreementExecutionDetailResponseSchema,
  MyAgreementRequirementsResponseSchema,
  SignatureUploadTargetResponseSchema,
  SigningSessionResponseSchema,
  SigningVerificationResponseSchema,
  StepUpAuthenticationResponseSchema,
  StepUpChallengeResponseSchema,
  StepUpRegistrationResponseSchema,
  AgreementExecutionResponseSchema,
} from '@/api/schemas/agreements';
import type {
  AcceptAgreementRequest,
  AgreementExecutionDetailResponse,
  AgreementExecutionResponse,
  BeginStepUpRequest,
  FinishStepUpRequest,
  IssueSignatureUploadRequest,
  MyAgreementRequirementsResponse,
  OpenSigningSessionRequest,
  SignAgreementRequest,
  SignatureUploadTargetResponse,
  SigningSessionResponse,
  SigningVerificationResponse,
  StepUpAuthenticationResponse,
  StepUpChallengeResponse,
  StepUpRegistrationResponse,
  VerifySigningSessionRequest,
} from '@/api/schemas/agreements.types';

/** The envelope every single-resource response carries. */
type Envelope<T> = { data: T; meta: { requestId: string } };

/**
 * The subject's own obligations.
 *
 * `subjectKind` defaults to `tenant` on the server; a personal customer passes `customer`.
 * This is the ONLY subject-facing list of obligations — there is no subject-facing
 * list-executions route, so signed-PDF history is built from this list.
 */
export const agreementsApi = {
  listMyRequirements: (subjectKind: 'tenant' | 'customer' = 'tenant') =>
    http
      .get<Envelope<MyAgreementRequirementsResponse>>(
        '/agreements/requirements',
        envelope(MyAgreementRequirementsResponseSchema),
        { query: { subjectKind } },
      )
      .then((r) => r.data),

  /** Read one execution by id — the subject's own detail, with events and the PDF URL. */
  getMyExecution: (executionId: string) =>
    http
      .get<Envelope<AgreementExecutionDetailResponse>>(
        `/agreements/executions/${executionId}`,
        envelope(AgreementExecutionDetailResponseSchema),
      )
      .then((r) => r.data),

  /** Accept an `acknowledgment`/`consent` requirement. */
  accept: (executionId: string, input: AcceptAgreementRequest) =>
    http
      .post<Envelope<AgreementExecutionResponse>>(
        `/agreements/executions/${executionId}/acceptance`,
        envelope(AgreementExecutionResponseSchema),
        { body: input, idempotencyKey: generateIdempotencyKey() },
      )
      .then((r) => r.data),

  /** Decline a requirement. */
  decline: (executionId: string) =>
    http
      .post<Envelope<AgreementExecutionResponse>>(
        `/agreements/executions/${executionId}/decline`,
        envelope(AgreementExecutionResponseSchema),
        { idempotencyKey: generateIdempotencyKey() },
      )
      .then((r) => r.data),

  /** Open a signing session. The response carries the challenge and the content binding. */
  openSigningSession: (executionId: string, input: OpenSigningSessionRequest) =>
    http
      .post<Envelope<SigningSessionResponse>>(
        `/agreements/executions/${executionId}/signing-sessions`,
        envelope(SigningSessionResponseSchema),
        { body: input, idempotencyKey: generateIdempotencyKey() },
      )
      .then((r) => r.data),

  /** Request a presigned upload target for the signature image. */
  issueSignatureUpload: (executionId: string, input: IssueSignatureUploadRequest) =>
    http
      .post<Envelope<SignatureUploadTargetResponse>>(
        `/agreements/executions/${executionId}/signature-upload`,
        envelope(SignatureUploadTargetResponseSchema),
        { body: input, idempotencyKey: generateIdempotencyKey() },
      )
      .then((r) => r.data),

  /** Bind a live verification to the signing session. Single-use. */
  verifySigningSession: (executionId: string, input: VerifySigningSessionRequest) =>
    http
      .post<Envelope<SigningVerificationResponse>>(
        `/agreements/executions/${executionId}/signing-verification`,
        envelope(SigningVerificationResponseSchema),
        { body: input, idempotencyKey: generateIdempotencyKey() },
      )
      .then((r) => r.data),

  /** Sign. The `contentHash` must equal the session's, or the server refuses with CONTENT_CHANGED. */
  sign: (executionId: string, input: SignAgreementRequest) =>
    http
      .post<Envelope<AgreementExecutionResponse>>(
        `/agreements/executions/${executionId}/signature`,
        envelope(AgreementExecutionResponseSchema),
        { body: input, idempotencyKey: generateIdempotencyKey() },
      )
      .then((r) => r.data),

  /* ------------------------------------------------------------------------------------------
   * Fresh-authentication (step-up) ceremony — the vendor/customer-accessible path
   * ---------------------------------------------------------------------------------------- */

  beginStepUpRegistration: (executionId: string, input: BeginStepUpRequest) =>
    http
      .post<Envelope<StepUpChallengeResponse>>(
        `/agreements/executions/${executionId}/step-up/registration`,
        envelope(StepUpChallengeResponseSchema),
        { body: input, idempotencyKey: generateIdempotencyKey() },
      )
      .then((r) => r.data),

  finishStepUpRegistration: (executionId: string, input: FinishStepUpRequest) =>
    http
      .post<Envelope<StepUpRegistrationResponse>>(
        `/agreements/executions/${executionId}/step-up/registration/complete`,
        envelope(StepUpRegistrationResponseSchema),
        { body: input, idempotencyKey: generateIdempotencyKey() },
      )
      .then((r) => r.data),

  beginStepUpAuthentication: (executionId: string, input: BeginStepUpRequest) =>
    http
      .post<Envelope<StepUpChallengeResponse>>(
        `/agreements/executions/${executionId}/step-up/authentication`,
        envelope(StepUpChallengeResponseSchema),
        { body: input, idempotencyKey: generateIdempotencyKey() },
      )
      .then((r) => r.data),

  finishStepUpAuthentication: (executionId: string, input: FinishStepUpRequest) =>
    http
      .post<Envelope<StepUpAuthenticationResponse>>(
        `/agreements/executions/${executionId}/step-up/authentication/complete`,
        envelope(StepUpAuthenticationResponseSchema),
        { body: input, idempotencyKey: generateIdempotencyKey() },
      )
      .then((r) => r.data),
};

/**
 * Wraps a payload schema in the `{ data, meta }` envelope.
 *
 * The transport parses the WHOLE body, so the envelope is described here rather than
 * unwrapped by hand at each call site.
 */
function envelope<T extends z.ZodTypeAny>(schema: T) {
  return z.object({ data: schema, meta: ResponseMetaSchema });
}
